<template>
    <div class="xl:min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div :class="['authentication max-w-md w-full mx-auto p-8 bg-white/95 backdrop-blur-sm border border-white/20 rounded-2xl shadow-2xl', { 'flip-horizontal': showRegister }]">
            <h1 class="text-3xl font-bold mb-8 text-center text-paletteDarkGreen">{{ $t('login.title') }}</h1>
            <form @submit.prevent="loginWithEmail" class="space-y-6" novalidate>
                <div v-if="errorMessage" class="mb-4 p-3 text-red-700 bg-red-100 border border-red-400 rounded-lg text-sm">
                    {{ errorMessage }}
                </div>
                <div>
                    <label for="email" :class="['block text-sm font-medium mb-1', validationState.email ? 'text-gray-700' : 'text-red-700']">{{ $t('login.emailLabel') }}</label>
                    <input id="email" type="email" v-model="email" @input="validateOnInput('email')"
                        :class="['mt-1 block w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 sm:text-sm transition duration-200',
                        validationState.email ? 'border-gray-300 focus:ring-paletteDarkGreen focus:border-paletteDarkGreen' : 'border-red-500 bg-red-50 text-red-900 placeholder-red-700 focus:ring-red-500 focus:border-red-500']" />
                    <p v-if="!validationState.email" class="mt-2 text-sm text-red-600">
                        {{ $t('login.emailError') }}
                    </p>
                </div>
                <div>
                    <label for="password" :class="['block text-sm font-medium mb-1', validationState.password ? 'text-gray-700' : 'text-red-700']">{{ $t('login.passwordLabel') }}</label>
                    <div class="relative">
                        <input id="password" :type="showPassword ? 'text' : 'password'" v-model="password" @input="validateOnInput('password')"
                            :class="['mt-1 block w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 sm:text-sm transition duration-200',
                            validationState.password ? 'border-gray-300 focus:ring-paletteDarkGreen focus:border-paletteDarkGreen' : 'border-red-500 bg-red-50 text-red-900 placeholder-red-700 focus:ring-red-500 focus:border-red-500']" />
                        <button type="button" @click="togglePasswordVisibility"
                            class="absolute inset-y-0 right-0 pr-3 flex items-center mt-1">
                            <svg v-if="!showPassword" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-gray-500 hover:text-gray-700">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-gray-500 hover:text-gray-700">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                            </svg>
                        </button>
                    </div>
                    <p v-if="!validationState.password" class="mt-2 text-sm text-red-600">
                        {{ $t('login.passwordError') }}
                    </p>
                </div>
                <button type="submit"
                    :disabled="isLoading"
                    class="w-full py-3 px-4 border border-transparent rounded-lg shadow-lg text-sm font-medium text-white bg-paletteDarkGreen hover:bg-paletteLightGreen focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-paletteDarkGreen transition duration-300 transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center">
                    <LoaderIcon v-if="isLoading" />
                    <span v-else>{{ $t('login.submitButton') }}</span>
                </button>
            </form>
            <div class="mt-4 text-center">
                <a @click="showForgotPasswordModal = true" class="text-sm text-paletteDarkGreen hover:text-paletteLightGreen cursor-pointer font-medium transition duration-200">
                    {{ $t('login.forgotPassword') }}
                </a>
            </div>

            <!-- Forgot Password Modal -->
            <div v-if="showForgotPasswordModal" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                <div class="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
                    <h2 class="text-lg font-bold mb-4 text-center">{{ $t('login.forgotPasswordTitle') }}</h2>
                    <p class="text-sm text-gray-600 mb-4">{{ $t('login.forgotPasswordInstructions') }}</p>
                    <div>
                        <label for="resetEmail" class="block text-sm font-medium text-gray-700">{{ $t('login.emailLabel') }}</label>
                        <input id="resetEmail" type="email" v-model="resetEmail"
                            class="mt-1 block w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 sm:text-sm transition duration-200 border-gray-300 focus:ring-paletteDarkGreen focus:border-paletteDarkGreen" />
                    </div>
                    <div v-if="resetErrorMessage" class="mt-2 text-sm text-red-600">
                        {{ resetErrorMessage }}
                    </div>
                    <div v-if="resetSuccessMessage" class="mt-2 text-sm text-green-600">
                        {{ resetSuccessMessage }}
                    </div>
                    <div class="mt-6 flex justify-end gap-4">
                        <button @click="showForgotPasswordModal = false" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300">
                            {{ $t('login.cancel') }}
                        </button>
                        <button @click="sendPasswordReset" class="px-4 py-2 text-sm font-medium text-white bg-paletteDarkGreen rounded-lg hover:bg-paletteLightGreen">
                            {{ $t('login.sendResetEmail') }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="relative my-8">
                <div class="absolute inset-0 flex items-center">
                    <div class="w-full border-t border-gray-300"></div>
                </div>
                <div class="relative flex justify-center text-sm">
                    <span class="px-2 backdrop-blur-sm text-gray-500">{{ $t('login.orLoginWith') }}</span>
                </div>
            </div>
            <div class="social-login flex justify-center gap-4">
                <button @click="loginWithGoogle"
                    class="flex items-center justify-center w-12 h-12 bg-white border border-gray-300 rounded-lg shadow-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200 transform hover:-translate-y-1">
                    <div class="gsi-material-button-state"></div>
                    <div class="gsi-material-button-content-wrapper">
                        <div class="gsi-material-button-icon">
                            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" xmlns:xlink="http://www.w3.org/1999/xlink" class="w-6 h-6">
                                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                                <path fill="none" d="M0 0h48v48H0z"></path>
                            </svg>
                        </div>
                        <span class="sr-only">{{ $t('login.googleAlt') }}</span>
                    </div>
                </button>
            </div>
            <div class="mt-8 text-center">
                <p class="text-sm text-gray-700">{{ $t('login.noAccount') }}
                    <a @click="toggleRegister" class="text-paletteDarkGreen hover:text-paletteLightGreen cursor-pointer font-medium transition duration-200">{{ $t('login.registerLink') }}</a>
                </p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/users'
import { useI18n } from 'vue-i18n'
import { getAuth, signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, sendPasswordResetEmail } from 'firebase/auth'
// import { useUserStore } from '@/stores/users'
import LoaderIcon from '@/components/utils/LoaderIcon.vue'

defineOptions({ name: 'AuthenticationLogin' })

const email = ref('')
const password = ref('')
const showRegister = ref(false)
const showPassword = ref(false)
const isLoading = ref(false)
const validationState = reactive({
  email: true,
  password: true
})
const errorMessage = ref('')
const showForgotPasswordModal = ref(false)
const resetEmail = ref('')
const resetErrorMessage = ref('')
const resetSuccessMessage = ref('')

const router = useRouter()
const userStore = useUserStore()
const { t } = useI18n()
// const userStore = useUserStore()

function getReadableErrorMessage (error) {
  const errorCode = error.code || error.message
  const errorMessages = {
    'auth/invalid-credential': t('login.invalidCredentials'),
    'auth/user-not-found': t('login.userNotFound'),
    'auth/wrong-password': t('login.wrongPassword'),
    'auth/invalid-email': t('login.invalidEmail'),
    'auth/user-disabled': t('login.userDisabled'),
    'auth/too-many-requests': t('login.tooManyAttempts'),
    'auth/network-request-failed': t('login.networkError'),
    'auth/popup-closed-by-user': t('login.popupClosed')
  }
  return errorMessages[errorCode] || t('login.defaultError')
}

function validateOnInput (field) {
  if (field === 'email') {
    validationState.email = email.value && validateEmail(email.value)
  } else if (field === 'password') {
    validationState.password = password.value
  }
  if (errorMessage.value) {
    errorMessage.value = ''
  }
}

function validateEmail (emailValue) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(emailValue)
}

async function loginWithEmail () {
  validationState.email = true
  validationState.password = true
  errorMessage.value = ''
  validationState.email = email.value && validateEmail(email.value)
  validationState.password = password.value
  if (!validationState.email || !validationState.password) {
    errorMessage.value = t('login.validationError')
    return
  }
  isLoading.value = true
  const auth = getAuth()
  try {
    await signInWithEmailAndPassword(auth, email.value, password.value)
    // const token = await userCredential.user.getIdToken()
    // await userStore.authenticateUserWithBackend(token)
    await waitForAuth()
    router.push({ name: 'Dashboard' })
  } catch (error) {
    errorMessage.value = getReadableErrorMessage(error)
  } finally {
    isLoading.value = false
  }
}

async function loginWithGoogle () {
  const auth = getAuth()
  const provider = new GoogleAuthProvider()
  isLoading.value = true
  try {
    await signInWithPopup(auth, provider)
    await waitForAuth()
    router.push({ name: 'Dashboard' })
  } catch (error) {
    errorMessage.value = getReadableErrorMessage(error)
  } finally {
    isLoading.value = false
  }
}

async function sendPasswordReset () {
  const auth = getAuth()
  resetErrorMessage.value = ''
  resetSuccessMessage.value = ''
  if (!validateEmail(resetEmail.value)) {
    resetErrorMessage.value = t('login.invalidEmail')
    return
  }
  try {
    await sendPasswordResetEmail(auth, resetEmail.value)
    resetSuccessMessage.value = t('login.resetEmailSent')
  } catch (error) {
    resetErrorMessage.value = getReadableErrorMessage(error)
  }
}

function toggleRegister () {
  showRegister.value = !showRegister.value
  setTimeout(() => {
    router.push('/register')
  }, 500)
}

function togglePasswordVisibility () {
  showPassword.value = !showPassword.value
}

// Helper: Wait for userStore.isAuthenticated to become true
function waitForAuth () {
  return new Promise(resolve => {
    if (userStore.isAuthenticated) return resolve()
    watch(
      () => userStore.user,
      (val) => {
        if (val) {
          // stop()
          resolve()
        }
      }
    )
  })
}
</script>

<style scoped>
.authentication {
    transition: transform 0.6s;
    transform-style: preserve-3d;
}

.flip-horizontal {
    transform: rotateY(180deg);
}

.bg-gradient-to-br {
    background-size: 200% 200%;
    animation: gradient 15s ease infinite;
}

@keyframes gradient {
    0% {
        background-position: 0% 50%;
    }
    50% {
        background-position: 100% 50%;
    }
    100% {
        background-position: 0% 50%;
    }
}

.gsi-material-button {
  -moz-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  -webkit-appearance: none;
  background-color: WHITE;
  background-image: none;
  border: 1px solid #747775;
  -webkit-border-radius: 4px;
  border-radius: 4px;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  color: #1f1f1f;
  cursor: pointer;
  font-family: 'Roboto', arial, sans-serif;
  font-size: 14px;
  height: 40px;
  letter-spacing: 0.25px;
  outline: none;
  overflow: hidden;
  padding: 0;
  position: relative;
  text-align: center;
  -webkit-transition: background-color .218s, border-color .218s, box-shadow .218s;
  transition: background-color .218s, border-color .218s, box-shadow .218s;
  vertical-align: middle;
  white-space: nowrap;
  width: 40px;
  max-width: 400px;
  min-width: min-content;
}

.gsi-material-button .gsi-material-button-icon {
  height: 20px;
  margin-right: 12px;
  min-width: 20px;
  width: 20px;
  margin: 0;
  padding: 9px;
}

.gsi-material-button .gsi-material-button-content-wrapper {
  -webkit-align-items: center;
  align-items: center;
  display: flex;
  -webkit-flex-direction: row;
  flex-direction: row;
  -webkit-flex-wrap: nowrap;
  flex-wrap: nowrap;
  height: 100%;
  justify-content: space-between;
  position: relative;
  width: 100%;
}

.gsi-material-button .gsi-material-button-contents {
  -webkit-flex-grow: 1;
  flex-grow: 1;
  font-family: 'Roboto', arial, sans-serif;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: top;
}

.gsi-material-button .gsi-material-button-state {
  -webkit-transition: opacity .218s;
  transition: opacity .218s;
  bottom: 0;
  left: 0;
  opacity: 0;
  position: absolute;
  right: 0;
  top: 0;
}

.gsi-material-button:disabled {
  cursor: default;
  background-color: #ffffff61;
  border-color: #1f1f1f1f;
}

.gsi-material-button:disabled .gsi-material-button-contents {
  opacity: 38%;
}

.gsi-material-button:disabled .gsi-material-button-icon {
  opacity: 38%;
}

.gsi-material-button:not(:disabled):active .gsi-material-button-state,
.gsi-material-button:not(:disabled):focus .gsi-material-button-state {
  background-color: #303030;
  opacity: 12%;
}

.gsi-material-button:not(:disabled):hover {
  -webkit-box-shadow: 0 1px 2px 0 rgba(60, 64, 67, .30), 0 1px 3px 1px rgba(60, 64, 67, .15);
  box-shadow: 0 1px 2px 0 rgba(60, 64, 67, .30), 0 1px 3px 1px rgba(60, 64, 67, .15);
}

.gsi-material-button:not(:disabled):hover .gsi-material-button-state {
  background-color: #303030;
  opacity: 8%;
}
</style>
